import React, { useCallback, useState } from 'react';

import { analytics } from '@sy/app-client';
import { MarkDown } from '@sy/component-library';

import type { MarkDownComponents } from '@sy/component-library';

import type { CacheBlogPost } from '~/services/contentful/model/ContentfulBlogPost';
import { events } from '~/services/tracking';
import {
	ContentWrapper,
	StyledAnchor,
	StyledHeading1,
	StyledHeading2,
	StyledHeading3,
	StyledHeading4,
	StyledHeading5,
	StyledHeading6,
	StyledHorizontalRule,
	StyledIframe,
	StyledImageContainer,
	StyledImage,
	StyledInstagramContainer,
	StyledListItem,
	StyledOrderedList,
	StyledParagraph,
	StyledPre,
	StyledUnorderedList,
} from './Content.styles';

const Anchor = ({ href, ...props }: { href?: string }) => {
	const trackAnchorClick = useCallback(() => {
		analytics.track(events.MAIN_CONTENT_INTERACTION_CLICKED, {
			contentType: 'link',
			destinationUrl: href,
		});
	}, [href]);

	return <StyledAnchor {...props} href={href} onClick={trackAnchorClick} />;
};

const YouTubeIframe = ({ src, ...props }: { src?: string }) => {
	const [clicked, setClicked] = useState(false);

	const trackVideoClick = useCallback(() => {
		if (clicked) {
			return;
		}

		analytics.track(events.MAIN_CONTENT_INTERACTION_CLICKED, {
			contentType: 'video',
			destinationUrl: src,
		});
		setClicked(true);
	}, [clicked, src]);

	if (!src || !src.startsWith('https://www.youtube.com')) {
		return null;
	}

	return <StyledIframe src={src} {...props} onClick={trackVideoClick} />;
};

const BlockQuote = (props: React.HTMLAttributes<HTMLQuoteElement>) => (
	<StyledInstagramContainer>
		<blockquote {...props} />
	</StyledInstagramContainer>
);

const Image = ({ src }: { src?: string }) => (
	<StyledImageContainer>
		<StyledImage alt="" src={src?.startsWith('http') ? src : `https:${src}`} />
	</StyledImageContainer>
);

const components = {
	a: Anchor,
	blockquote: BlockQuote,
	h1: StyledHeading1,
	h2: StyledHeading2,
	h3: StyledHeading3,
	h4: StyledHeading4,
	h5: StyledHeading5,
	h6: StyledHeading6,
	hr: StyledHorizontalRule,
	iframe: YouTubeIframe,
	img: Image,
	li: StyledListItem,
	ol: StyledOrderedList,
	p: StyledParagraph,
	pre: StyledPre,
	ul: StyledUnorderedList,
} as typeof MarkDownComponents;

const Content = ({ post: { content } }: Props) => {
	return content ? (
		<ContentWrapper>
			<MarkDown components={components} content={content} />
		</ContentWrapper>
	) : null;
};
type Props = {
	post: CacheBlogPost;
};

export default Content;
